import { useEffect, useMemo, useState } from 'react';

import { MAX_EXPIRY_CHECK_MS } from './constants';

// Returns a `now` which is refreshed whenever the settings change and again at
// the next upcoming endsOn, so anything derived from isMaintenanceModeActive
// re-evaluates the moment a maintenance mode expires - no reload or staff
// action required. `settingsList` should be memoized by the caller.
const useMaintenanceModeClock = (
  settingsList: (MaintenanceMode.Settings | undefined)[],
) => {
  const [tick, setTick] = useState<number>(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [settingsList, tick]);

  useEffect(() => {
    const current = Date.now();
    const upcomingEnds = settingsList
      .filter(
        (s): s is MaintenanceMode.Settings =>
          !!s && s.isEnabled && s.endsOn !== null,
      )
      .map((s) => new Date(s.endsOn as string).getTime())
      .filter((endsOn) => endsOn > current);

    if (upcomingEnds.length === 0) {
      return;
    }

    const delay = Math.min(
      Math.min(...upcomingEnds) - current,
      MAX_EXPIRY_CHECK_MS,
    );
    const timeout = setTimeout(() => setTick((t) => t + 1), delay);
    return () => {
      clearTimeout(timeout);
    };
  }, [settingsList, tick]);

  return now;
};

export default useMaintenanceModeClock;
